import React, { useEffect, useState } from "react";
import {
  Facebook,
  Twitter,
  Youtube,
  Instagram,
  Linkedin,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  ArrowRight,
  Shield,
  MessageCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { http } from "../../../axios/axios";
import { FaAddressBook, FaWhatsapp } from "react-icons/fa";

const logo = "/Suraksha24x7logo.png";

const socialLinks = [
  {
    name: "Facebook",
    icon: Facebook,
    link: " https://www.facebook.com/share/189G59t49u/",
  },
  {
    name: "Instagram",
    icon: Instagram,
    link: " https://www.instagram.com/suraksha24x7?igsh=MXFhMGxrcndraTRvMw==",
  },
  {
    name: "LinkedIn",
    icon: Linkedin,
    link: " https://www.linkedin.com/in/sunil-kumar-a40011415?utm_source=share_via&utm_content=profile&utm_medium=member_android",
  },
  {
    name: "Whatsapp",
    icon: FaWhatsapp,
    link: "https://whatsapp.com/channel/0029VahBCy3I7Be8yd7Cl42g",
  },
  {
    name: "YouTube",
    icon: Youtube,
    link: " https://youtube.com/@suraksha24x7?si=BPYtqvsn_8FoiVpA",
  },
];

const Footer = () => {
  const [solutions, setSolutions] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const [solutionsRes, servicesRes] = await Promise.all([
          http.get("/solutions"),
          http.get("/services"),
        ]);

        setSolutions(solutionsRes.data?.data || []);
        setServices(servicesRes.data?.data || []);
      } catch (err) {
        console.error("Footer API error:", err);
      }
    };

    fetchFooterData();
  }, []);

  return (
    <footer className="relative w-full bg-[#0B1F33] overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-blue-500/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-amber-500/10 blur-[120px]" />
      </div>

      <div className="relative w-full bg-[#0B1F33] px-5 sm:px-6 lg:px-8 xl:px-16 py-14 lg:py-16">
        {/* Company Section */}
        <div className="mb-12">
          <Link
            to="/"
            onClick={() => {
              window.scrollTo({
                top: 0,
                left: 0,
                behavior: "smooth",
              });
            }}
            className="inline-block"
          >
            <img
              src={logo}
              alt="Suraksha 24/7"
              className="h-20 lg:h-28 w-auto mb-5 cursor-pointer"
            />
          </Link>

          <p className="text-slate-300 text-sm lg:text-[15px] leading-7">
            Suraksha 24X7 is a trusted security, surveillance and IT solutions
            company delivering advanced technology for homes, offices,
            societies, commercial spaces, warehouses, industries, schools,
            hospitals and institutions. We provide complete Security Products &
            Solutions including CCTV surveillance, AI-powered cameras, DVR/NVR
            systems, access control, biometric attendance, video door phones,
            intrusion alarms, GPS tracking and home automation. Our IT Products
            & Solutions include servers, laptops, networking, data storage,
            structured cabling, fiber optics, enterprise Wi-Fi, network
            security, UPS systems, IP telephony and computer peripherals. We
            deliver reliable, scalable and professionally integrated solutions
            for modern security and IT infrastructure.
          </p>
        </div>

        <div className="grid grid-cols-1 items-stretch gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-[minmax(115px,.75fr)_minmax(145px,1fr)_minmax(145px,1fr)_minmax(115px,.7fr)_auto] lg:gap-5 xl:grid-cols-[minmax(140px,.75fr)_minmax(190px,1fr)_minmax(190px,1fr)_minmax(130px,.7fr)_auto] xl:gap-8">
          <div className="min-w-0">
            <h3 className="text-white text-sm lg:text-base font-semibold uppercase tracking-[2px] mb-5">
              Quick Links
            </h3>

            <ul className="space-y-3">
              <li>
                <Link
                  to="/about"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  About Us
                </Link>
              </li>

              <li>
                <Link
                  to="/security-product"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  Security Products
                </Link>
              </li>

              <li>
                <Link
                  to="/it-products"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  IT Products
                </Link>
              </li>

              <li>
                <Link
                  to="/reviews"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  Reviews
                </Link>
              </li>

              <li>
                <Link
                  to="/blogs"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  Blogs
                </Link>
              </li>

              <li>
                <Link
                  to="/projects"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  Projects
                </Link>
              </li>

              <li>
                <Link
                  to="/careers"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  Careers
                </Link>
              </li>
            </ul>
          </div>
          <div className="min-w-0">
            <h3 className="text-white text-sm lg:text-base font-semibold uppercase tracking-[2px] mb-5">
              Solutions
            </h3>

            <ul className="space-y-3">
              {solutions.slice(0, 7).map((item) => (
                <li key={item._id}>
                  <Link
                    to={`/solutions/${item.slug}`}
                    className="flex items-start gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                  >
                    <ChevronRight size={15} className="mt-1 shrink-0" />
                    <span className="min-w-0 wrap-break-word">
                      {item.bannerTitle}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="min-w-0">
            <h3 className="text-white text-sm lg:text-base font-semibold uppercase tracking-[2px] mb-5">
              Services
            </h3>

            <ul className="space-y-3">
              {services.slice(0, 7).map((item) => (
                <li key={item._id}>
                  <Link
                    to={`/services/${item.slug}`}
                    className="flex items-start gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                  >
                    <ChevronRight size={15} className="mt-1 shrink-0" />
                    <span className="min-w-0 wrap-break-word">
                      {item.bannerTitle}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div className="min-w-0">
            <h3 className="text-white text-sm lg:text-base font-semibold uppercase tracking-[2px] mb-5">
              Support
            </h3>

            <ul className="space-y-3">
              <li>
                <Link
                  to="/contact"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  Contact Us
                </Link>
              </li>

              <li>
                <Link
                  to="/complaint"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  Complaint
                </Link>
              </li>

              <li>
                <Link
                  to="/product-inquiry"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition"
                >
                  <ChevronRight size={15} />
                  Product Inquiry
                </Link>
              </li>
            </ul>

            {/* Quick Links */}

            {/* <div className="space-y-6">
 
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 shrink-0 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Phone size={18} className="text-amber-400" />
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400">
                  Helpline
                </p>

                <a
                  href="tel:8448341881"
                  className="text-white hover:text-amber-400 transition"
                >
                  844-834-1881
                </a>
              </div>
            </div>


            <div className="flex items-start gap-4">
              <div className="w-11 h-11 shrink-0 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Mail size={18} className="text-blue-400" />
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400">
                  Email
                </p>

                <a
                  href="mailto:support@suraksha247.com"
                  className="text-white hover:text-blue-400 transition break-all"
                >
                  support@suraksha247.com
                </a>
              </div>
            </div>


            <div className="flex items-start gap-4">
              <div className="w-11 h-11 shrink-0 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <MapPin size={18} className="text-blue-400" />
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400">
                  Address
                </p>

                <p className="text-white text-sm leading-6">
                  Chanakya Place,
                  <br />
                  Opp. Mata Chanan Devi Hospital,
                  <br />
                  Janakpuri, New Delhi – 110058
                </p>
              </div>
            </div>
          </div> */}
          </div>

          {/* Support Image */}
          <div className="col-span-full flex w-full items-center justify-center self-stretch justify-self-center lg:col-span-1">
  <div className="relative mx-auto w-45 max-w-full lg:w-52.5 xl:w-57.5">
  <a
    href="tel:+918448341881"
    aria-label="Call Suraksha 24X7 Help Desk"
    className="group block w-full"
  >
    <div className="relative w-full overflow-visible pt-12">
      <img
        src="https://res.cloudinary.com/nemfcgrj/image/upload/v1790766283/ChatGPT_Image_Sep_30_2026_04_34_18_PM.png"
        alt="Suraksha 24/7 security support"
        loading="lazy"
        className="
          mx-auto
          block
          h-auto
          w-full
          object-contain
          transition-transform
          duration-200
          group-hover:scale-[1.18]
        "
      />

      <div className="help-desk-bubble absolute right-2 top-0 z-20 max-w-48 lg:max-w-58">
        <div className=" block
          h-auto
          w-full
          object-contain
          transition-transform
          duration-200
          group-hover:scale-[1.09]
        relative rounded-xl border border-white/60 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm">
          <div className="flex items-start gap-2 text-sm font-semibold leading-snug text-slate-800">
            <MessageCircle
              size={15}
              className="mt-0.5 shrink-0 text-blue-600"
              aria-hidden="true"
            />

            <span>Click Help Desk</span>
          </div>

          <span
            className="
              absolute
              -bottom-1.5
              right-5
              h-3
              w-3
              rotate-45
              border-b
              border-r
              border-white/60
              bg-white
            "
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  </a>
</div>
         {/*  <div className="flex w-45 max-w-full items-center justify-center self-stretch justify-self-center lg:w-52.5 xl:w-57.5">
            <div className="relative w-full overflow-visible pt-12">
              <img
                src="https://res.cloudinary.com/nemfcgrj/image/upload/v1790766283/ChatGPT_Image_Sep_30_2026_04_34_18_PM.png"
                alt="Suraksha 24/7 security support"
                loading="lazy"
                className="block h-auto w-full object-contain"
              />

              <div className="help-desk-bubble absolute right-2 -top-0 z-20 max-w-48 lg:max-w-58">
                <div className="relative rounded-xl border border-white/60 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm">
                  <div className="flex items-start gap-2 text-s font-semibold leading-snug text-slate-800">
                    <MessageCircle
                      size={15}
                      className="mt-0.5 shrink-0 text-blue-600"
                      aria-hidden="true"
                    />

                    <span>Click Help Desk</span>
                  </div>

                  <span
                    className="
          absolute
          -bottom-1.5
          right-5
          h-3
          w-3
          rotate-45
          border-b
          border-r
          border-white/60
          bg-white
        "
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>
          </div> */}
        </div>
</div>
        {/* MARQUEE */}
        <div className="hidden py-6 pb-[-5px] md:flex flex-1 min-w-0 overflow-hidden mx-6">
          <div className="flex whitespace-nowrap animate-marquee gap-12 text-white text-xs lg:text-lg font-extrabold font-semibold">
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
            <span>🔒 सुरक्षा 24/7, सुरक्षा हर वक़्त हर जगह</span>
          </div>
        </div>

        {/* Bottom */}
        <div className=" pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            {socialLinks.map(({ icon, link, name }) => {
              const content = React.createElement(icon, { size: 18 });

              return link ? (
                <a
                  key={name}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:bg-blue-600 hover:border-blue-600 hover:text-white transition"
                >
                  {content}
                </a>
              ) : (
                <div
                  key={name}
                  aria-label={name}
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-500 cursor-default"
                >
                  {content}
                </div>
              );
            })}
          </div>

          <p className="text-sm text-slate-400 text-center">
            © {new Date().getFullYear()}{" "}
            <span className="text-white font-semibold">SURAKSHA 24/7</span>. All
            Rights Reserved.
          </p>
        </div>
        
      </div>
    </footer>
  );
};

export default Footer;
